import {Thresholds as ThresholdsValues} from '@hconnect/common/types'
import React, {FC, useMemo} from 'react'
import {useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {useEditKpiThresholds} from '../../hooks'
import {ThresholdsData} from '../../types'
import {getKpiThresholdsConfig, getThresholdSeverityOptions, KpiThresholdsConfig} from '../../utils'

import {Thresholds} from '@settings/modules/common/components'
import {useUrlParam} from '@settings/modules/routing'

type ThresholdCardProps = {
  unit: string | null
  kpiId: number
  thresholds: Partial<ThresholdsValues>
}

export const ThresholdCard: FC<ThresholdCardProps> = ({unit, kpiId, thresholds}) => {
  const {t} = useTranslation()
  const plantCode = useUrlParam('plantCode')
  const {mutate: editKpiThresholds} = useEditKpiThresholds()

  const plantThresholdsConfig = useMemo<KpiThresholdsConfig[]>(
    () => getKpiThresholdsConfig(t, unit),
    [t, unit]
  )

  const thresholdSeverityOptions = useMemo(() => getThresholdSeverityOptions(t), [t])

  const defaultValues: ThresholdsData = {
    thresholds: thresholds
  }

  const {
    handleSubmit,
    control,
    watch,
    getValues,
    setValue,
    reset,
    formState: {isDirty, isSubmitting}
  } = useForm<ThresholdsData>({
    defaultValues,
    shouldFocusError: false
  })

  const submit = ({thresholds}: ThresholdsData) => {
    if (isDirty && !isSubmitting) {
      editKpiThresholds({thresholds: prepareThresholdsForSubmission(thresholds), kpiId, plantCode})
      reset(watch(), {keepValues: false, keepDirtyValues: false, keepErrors: false})
    }
  }

  return (
    <Thresholds
      title={t('kpiCalculationSettings.label.thresholds')}
      control={control}
      getValues={getValues}
      onSubmit={handleSubmit(submit)}
      thresholdsConfig={plantThresholdsConfig}
      setValue={setValue}
      statusOptions={thresholdSeverityOptions}
      enableThresholdsPreview
      CardBoxProps={{boxShadow: 'none'}}
    />
  )
}

const prepareThresholdsForSubmission = (thresholds: Partial<ThresholdsValues>) => {
  Object.keys(thresholds).forEach((key) => {
    const threshold = thresholds[key]
    if (threshold.value === '') {
      threshold.value = null
    }
    if (threshold.severity === '') {
      threshold.severity = null
    }
  })
  return thresholds
}
